import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Send, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { submitEventFeedback, PlatformIssueType } from '@/services/feedbackService';
import { ScrollArea } from '@/components/ui/scroll-area';

const platformFeedbackSchema = z.object({
  eventId: z.string().default('PLATFORM'), // Using 'PLATFORM' as a marker for platform feedback
  issueType: z.enum(['appearance', 'usability', 'feature_suggestion', 'general'] as const),
  description: z.string().min(10, {
    message: 'A leírásnak legalább 10 karakter hosszúnak kell lennie.',
  }),
  email: z.string().email({
    message: 'Érvényes e-mail címet adjon meg.',
  }).optional().or(z.literal('')),
  rating: z.number().min(1).max(5).optional(),
});

type PlatformFeedbackValues = z.infer<typeof platformFeedbackSchema>;

const feedbackTypeLabels = {
  appearance: 'Megjelenéssel kapcsolatos',
  usability: 'Használhatósággal kapcsolatos',
  feature_suggestion: 'Funkció javaslat',
  general: 'Általános visszajelzés',
};

interface PlatformFeedbackFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const PlatformFeedbackForm = ({ isOpen, onClose }: PlatformFeedbackFormProps) => {
  const { toast } = useToast();
  
  const form = useForm<PlatformFeedbackValues>({
    resolver: zodResolver(platformFeedbackSchema),
    defaultValues: {
      eventId: 'PLATFORM',
      issueType: 'general',
      description: '',
      email: '',
      rating: undefined,
    },
  });

  const onSubmit = async (values: PlatformFeedbackValues) => {
    try {
      console.log('Platform feedback submitted:', values);
      await submitEventFeedback({
        ...values,
        issueType: values.issueType as PlatformIssueType
      });
      
      toast({
        title: 'Köszönjük a visszajelzést!',
        description: 'Visszajelzésed rögzítettük, és hamarosan foglalkozunk vele.',
      });
      
      form.reset();
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: 'Hiba történt!',
        description: 'Nem sikerült elküldeni a visszajelzést. Kérjük, próbálja újra később.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 flex flex-col h-[calc(100vh-40px)] max-h-[600px] sm:h-auto">
        <DialogHeader className="p-4 sm:p-6 border-b">
          <DialogTitle className="text-xl">Visszajelzés a MediHub-ról</DialogTitle>
          <DialogDescription>
            Segíts nekünk jobbá tenni a MediHub-ot! Oszd meg velünk véleményedet, javaslataidat.
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="flex-1 overflow-auto">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} id="platform-feedback-form" className="p-4 sm:p-6 space-y-4">
              <FormField
                control={form.control}
                name="issueType"
                render={({ field }) => (
                  <FormItem className="space-y-3">
                    <FormLabel className="font-medium">Mi a visszajelzés típusa?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="space-y-1"
                      >
                        {Object.entries(feedbackTypeLabels).map(([value, label]) => (
                          <FormItem key={value} className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={value} />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer text-sm">
                              {label}
                            </FormLabel>
                          </FormItem>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">Részletes leírás</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Kérjük, írd le részletesen a véleményed vagy javaslataidat..."
                        className="min-h-[120px] resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Részletes visszajelzésed segít abban, hogy jobban megértsük igényeidet.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">Értékelés (1-5)</FormLabel>
                    <FormControl>
                      <div className="flex space-x-3">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <Button
                            key={rating}
                            type="button"
                            variant={field.value === rating ? "default" : "outline"}
                            className="w-10 h-10 p-0"
                            onClick={() => field.onChange(rating)}
                          >
                            {rating}
                          </Button>
                        ))}
                      </div>
                    </FormControl>
                    <FormDescription className="text-xs">
                      Hogyan értékeled a MediHub jelenlegi verzióját?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-medium">E-mail cím (nem kötelező)</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="pelda@email.hu"
                        {...field}
                        type="email"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      Adja meg e-mail címét, ha szeretne értesítést kapni a visszajelzésével kapcsolatban.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        </ScrollArea>
        
        <DialogFooter className="p-4 sm:p-6 border-t">
          <div className="flex w-full flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" type="button" onClick={onClose} className="w-full sm:w-auto flex-1 sm:flex-initial">
              <X className="mr-2 h-4 w-4" />
              Mégsem
            </Button>
            <Button type="submit" form="platform-feedback-form" className="w-full sm:w-auto flex-1 sm:flex-initial">
              <Send className="mr-2 h-4 w-4" />
              Küldés
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PlatformFeedbackForm;
